import React, { useEffect, useState, useCallback } from "react";
import {
  Activity as ActivityIcon,
  RefreshCw,
  LogIn,
  UserPlus,
  Users,
  Building2,
} from "lucide-react";
import { Select, ConfigProvider, theme as antdTheme } from "antd";
import { useApi } from "../../hooks/useApi";

/**
 * Admin — User Activity Summary.
 *
 * Self-contained tab rendered inside MainAdmin. Shows logins / new users /
 * active users for Today, Avg Last 7 Days, Avg Last 30 Days, plus a focused
 * row for a single organization. Talks only to the additive
 * /admin/activity-summary + /admin/organizations endpoints.
 */
const METRIC_META = {
  "Total Logins": { icon: LogIn, accent: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
  "New Users": { icon: UserPlus, accent: "#8b5cf6", bg: "rgba(139,92,246,0.12)" },
  "Active Users": { icon: Users, accent: "#00d1c1", bg: "rgba(0,209,193,0.12)" },
};

const ActivitySummary = ({ isDark = true }) => {
  const api = useApi();

  const [summary, setSummary] = useState(null);
  const [focused, setFocused] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await api.get("/api/v1/admin/organizations");
      setOrgs(res.data?.data?.organizations || []);
    } catch (_e) {
      /* non-fatal */
    }
  }, [api]);

  const fetchSummary = useCallback(
    async (focusOrgId) => {
      setLoading(true);
      setErr("");
      try {
        const res = await api.get("/api/v1/admin/activity-summary", {
          params: focusOrgId ? { orgId: focusOrgId } : {},
        });
        setSummary(res.data?.data?.summary || null);
        setFocused(res.data?.data?.focusedActiveUsers || null);
      } catch (e) {
        setErr(
          e?.response?.data?.error?.message ||
            "Failed to load activity summary."
        );
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  useEffect(() => {
    fetchOrgs();
    fetchSummary();
  }, [fetchOrgs, fetchSummary]);

  const onOrgChange = (value) => {
    const id = value || "";
    setOrgId(id);
    fetchSummary(id || undefined);
  };

  const text = isDark ? "#ffffff" : "#0f172a";
  const sub = isDark ? "#94a3b8" : "#64748b";
  const cardBg = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const cardBorder = isDark ? "rgba(255,255,255,0.08)" : "#e8ecf2";
  const rowBorder = isDark ? "rgba(255,255,255,0.05)" : "#eef1f5";
  const headBg = isDark ? "rgba(255,255,255,0.04)" : "#fafbfd";

  const rows = summary
    ? [
        { label: "Total Logins", d: summary.totalLogins },
        { label: "New Users", d: summary.newUsers },
        { label: "Active Users", d: summary.activeUsers },
      ]
    : [];

  const fmt = (v) =>
    typeof v === "number" ? v.toLocaleString("en-IN") : "—";

  return (
    <div style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span
            className="flex items-center justify-center"
            style={{
              width: 44,
              height: 44,
              borderRadius: 13,
              background: "linear-gradient(135deg, #00d1c1, #0f766e)",
              boxShadow: "0 8px 20px -6px rgba(0,209,193,0.5)",
            }}
          >
            <ActivityIcon className="h-5 w-5 text-white" />
          </span>
          <div>
            <h2 style={{ color: text, fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", margin: 0 }}>
              User Activity Summary
            </h2>
            <p style={{ color: sub, fontSize: 13.5, margin: "2px 0 0" }}>
              Logins, new users and active users across the platform.
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchSummary(orgId || undefined)}
          className="inline-flex items-center gap-2 transition-colors"
          style={{
            height: 40,
            padding: "0 16px",
            borderRadius: 11,
            fontSize: 13.5,
            fontWeight: 600,
            color: isDark ? "#cbd5e1" : "#475569",
            background: cardBg,
            border: `1px solid ${cardBorder}`,
          }}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {err && (
        <div className="mb-4 px-4 py-3 rounded-xl text-sm" style={{ background: "rgba(239,68,68,0.1)", color: "#f87171", border: "1px solid rgba(239,68,68,0.2)" }}>
          {err}
        </div>
      )}

      {/* Table card */}
      <div
        style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: 18,
          overflow: "hidden",
          boxShadow: isDark ? "none" : "0 1px 3px rgba(16,24,40,0.04)",
        }}
      >
        {/* Column header */}
        <div
          className="grid items-center"
          style={{
            gridTemplateColumns: "2fr 1fr 1.2fr 1.2fr",
            background: headBg,
            padding: "14px 24px",
            borderBottom: `1px solid ${cardBorder}`,
          }}
        >
          <span style={{ color: sub, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            Metric
          </span>
          <HeadCell sub={sub}>Today</HeadCell>
          <HeadCell sub={sub}>Avg · 7 Days</HeadCell>
          <HeadCell sub={sub}>Avg · 30 Days</HeadCell>
        </div>

        {loading && !summary ? (
          <div style={{ padding: "40px 24px", textAlign: "center", color: sub, fontSize: 14 }}>
            Loading…
          </div>
        ) : (
          <>
            {rows.map((r) => {
              const meta = METRIC_META[r.label] || {};
              const Icon = meta.icon || Users;
              return (
                <div
                  key={r.label}
                  className="grid items-center activity-row"
                  style={{
                    gridTemplateColumns: "2fr 1fr 1.2fr 1.2fr",
                    padding: "16px 24px",
                    borderBottom: `1px solid ${rowBorder}`,
                  }}
                >
                  <span className="flex items-center" style={{ gap: 12 }}>
                    <span
                      className="flex items-center justify-center"
                      style={{ width: 34, height: 34, borderRadius: 10, background: meta.bg, color: meta.accent, flex: "none" }}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span style={{ color: text, fontSize: 14.5, fontWeight: 600 }}>{r.label}</span>
                  </span>
                  <ValueCell text={text} sub={sub}>{fmt(r.d?.today)}</ValueCell>
                  <ValueCell text={text} sub={sub}>{fmt(r.d?.avg7)}</ValueCell>
                  <ValueCell text={text} sub={sub}>{fmt(r.d?.avg30)}</ValueCell>
                </div>
              );
            })}

            {/* Focused org row */}
            <div
              className="grid items-center"
              style={{
                gridTemplateColumns: "2fr 1fr 1.2fr 1.2fr",
                padding: "16px 24px",
                background: isDark ? "rgba(0,209,193,0.04)" : "rgba(0,209,193,0.03)",
              }}
            >
              <span className="flex items-center" style={{ gap: 12 }}>
                <span
                  className="flex items-center justify-center"
                  style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(245,158,11,0.12)", color: "#f59e0b", flex: "none" }}
                >
                  <Building2 className="h-4 w-4" />
                </span>
                <span className="flex items-center" style={{ gap: 8, flexWrap: "wrap" }}>
                  <span style={{ color: text, fontSize: 14.5, fontWeight: 600 }}>Active users in</span>
                  <ConfigProvider
                    theme={{
                      algorithm: isDark
                        ? antdTheme.darkAlgorithm
                        : antdTheme.defaultAlgorithm,
                      token: { colorPrimary: "#00d1c1", borderRadius: 9 },
                    }}
                  >
                    <Select
                      showSearch
                      allowClear
                      value={orgId || undefined}
                      onChange={onOrgChange}
                      placeholder="Select organization"
                      style={{ minWidth: 240 }}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={orgs.map((o) => ({
                        value: o._id,
                        label: o.name,
                      }))}
                    />
                  </ConfigProvider>
                </span>
              </span>
              {focused ? (
                <>
                  <ValueCell text={text} sub={sub} highlight>{fmt(focused.today)}</ValueCell>
                  <ValueCell text={text} sub={sub} highlight>{fmt(focused.avg7)}</ValueCell>
                  <ValueCell text={text} sub={sub} highlight>{fmt(focused.avg30)}</ValueCell>
                </>
              ) : (
                <>
                  <ValueCell text={sub} sub={sub}>—</ValueCell>
                  <ValueCell text={sub} sub={sub}>—</ValueCell>
                  <ValueCell text={sub} sub={sub}>—</ValueCell>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <p style={{ color: sub, fontSize: 12, marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
        <ActivityIcon className="h-3.5 w-3.5" />
        Logins are distinct users signed-in; active users are derived from
        tracking activity. 7/30-day columns are per-day averages.
      </p>

      <style>{`
        .activity-row:last-child { border-bottom: none; }
        .activity-row { transition: background 0.15s ease; }
        .activity-row:hover { background: ${isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.012)"}; }
      `}</style>
    </div>
  );
};

const HeadCell = ({ children, sub }) => (
  <span style={{ color: sub, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", textAlign: "right" }}>
    {children}
  </span>
);

const ValueCell = ({ children, text, sub, highlight }) => (
  <span
    style={{
      textAlign: "right",
      fontSize: highlight ? 17 : 16,
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: text,
      fontVariantNumeric: "tabular-nums",
    }}
  >
    {children}
  </span>
);

export default ActivitySummary;
